import { useState, useEffect, useCallback } from "react";
import { arrayMove } from "@dnd-kit/sortable";
import { PROGRAM_CARD_IDS, PROGRAM_COLORS, PROGRAM_SUMMARY_CARD_IDS, LENS_PRIORITY } from "./programLensConstants";

// Map agenda source_table values → card IDs that represent that data
const SOURCE_TABLE_TO_CARD_IDS = {
  encounter_sessions: ["encounters"],
  barriers: ["barriers"],
  goals: ["goals"],
  vitals: ["vitals"],
  appointments: ["appointments"],
  // care_gaps intentionally excluded — shown inline in pre-call agenda
  medications: ["medications"],
  assessments: ["assessments"],
  care_plans: ["carePlans"],
  program_enrollments: ["programEnrollment"],
  labs: ["labs"],
};

export const DEFAULT_COLUMNS = {
  col1: ["preCallSummary", "careTasks", "preventiveCare", "qualityAssurance", "socialDeterminants"],
  col2: ["goals", "problems", "medications", "allergies", "procedures"],
  col3: ["encounters", "notes", "appointments", "assessments", "labs", "vitals"],
  col4: ["socialHistory", "barriers", "resources", "programEnrollment", "immunizations"],
  col5: ["demographics", "carePlans", "deviceOrders", "rpmDeviceOrdering", "rpmDeviceReadings", "billingRecords"],
};

const ALL_CARD_IDS = Object.values(DEFAULT_COLUMNS).flat();
// Cards that no longer exist; dropped from layouts saved before they were removed
const REMOVED_CARD_IDS = new Set(["sopAssistant"]);
const ALWAYS_EXPANDED = new Set(["preCallSummary"]);

function getStorageKey(patientId) {
  return `chartLayout_v6_${patientId}`; // v6: every card starts open (older saved layouts started collapsed)
}

function extractAgendaCardIds(preCallData) {
  if (!preCallData) return new Set();
  const cardIds = new Set();
  const items = [
    ...(preCallData.agenda_items || []),
    ...(preCallData.tiers ? Object.values(preCallData.tiers).flat() : []),
  ];
  items.forEach((item) => {
    const mapped = SOURCE_TABLE_TO_CARD_IDS[item.source_table];
    if (mapped) mapped.forEach((id) => cardIds.add(id));
  });
  return cardIds;
}

export function useChartLayout(patientId, preCallData) {
  const storageKey = getStorageKey(patientId);

  const [columnOrders, setColumnOrders] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.columnOrders) {
          return Object.fromEntries(Object.entries(parsed.columnOrders).map(
            ([col, ids]) => [col, (ids || []).filter((id) => !REMOVED_CARD_IDS.has(id))]
          ));
        }
      }
    } catch {}
    return { ...DEFAULT_COLUMNS };
  });

  const [collapsed, setCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.collapsed) return parsed.collapsed;
      }
    } catch {}
    // Default: every card open
    const initial = {};
    ALL_CARD_IDS.forEach((id) => {
      initial[id] = false;
    });
    return initial;
  });

  const [activeLenses, setActiveLenses] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.activeLenses) return new Set(parsed.activeLenses);
      }
    } catch {}
    return new Set();
  });

  // When preCallData arrives, expand agenda cards and move them to the front
  useEffect(() => {
    if (!preCallData) return;
    const agendaIds = extractAgendaCardIds(preCallData);
    if (agendaIds.size === 0) return;

    setCollapsed((prev) => {
      const next = { ...prev };
      agendaIds.forEach((id) => { next[id] = false; });
      ALWAYS_EXPANDED.forEach((id) => { next[id] = false; });
      return next;
    });

    // Move agenda cards to the top of their columns
    setColumnOrders((prev) => {
      const next = { ...prev };
      for (const [col, items] of Object.entries(prev)) {
        const agendaInCol = items.filter((id) => agendaIds.has(id));
        if (agendaInCol.length === 0) continue;
        const rest = items.filter((id) => !agendaIds.has(id));
        next[col] = [...agendaInCol, ...rest];
      }
      return next;
    });
  }, [preCallData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(
        storageKey,
        JSON.stringify({ columnOrders, collapsed, activeLenses: Array.from(activeLenses) })
      );
    } catch {}
  }, [columnOrders, collapsed, activeLenses, storageKey]);

  // Toggle collapse state + auto-reorder so expanded items float to top
  const toggleCollapsed = useCallback((id) => {
    setCollapsed((prevCollapsed) => {
      const willBeCollapsed = !prevCollapsed[id];

      // Reorder within column: expanded → top, collapsed → bottom
      setColumnOrders((prevOrders) => {
        const col = Object.keys(prevOrders).find((c) => prevOrders[c].includes(id));
        if (!col) return prevOrders;

        const items = prevOrders[col].filter((i) => i !== id); // remove id temporarily

        if (willBeCollapsed) {
          // Collapsing → append to end
          return { ...prevOrders, [col]: [...items, id] };
        } else {
          // Expanding → insert after the last currently-expanded item
          let insertAt = 0;
          for (let i = 0; i < items.length; i++) {
            if (!prevCollapsed[items[i]]) {
              insertAt = i + 1;
            }
          }
          const result = [...items];
          result.splice(insertAt, 0, id);
          return { ...prevOrders, [col]: result };
        }
      });

      return { ...prevCollapsed, [id]: willBeCollapsed };
    });
  }, []);

  const expandAll = useCallback(() => {
    setCollapsed((prev) => {
      const next = { ...prev };
      ALL_CARD_IDS.forEach((id) => { next[id] = false; });
      return next;
    });
    // Move all cards to their natural order (expanded state, sort alphabetically or just keep current)
  }, []);

  const collapseAll = useCallback(() => {
    setCollapsed((prev) => {
      const next = { ...prev };
      ALL_CARD_IDS.forEach((id) => {
        next[id] = !ALWAYS_EXPANDED.has(id);
      });
      return next;
    });
  }, []);

  // Handles both within-column reorder and cross-column moves atomically on drop
  const handleDragOver = useCallback(() => {}, []); // no-op: intermediate updates cause SortableContext crashes

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setColumnOrders((prev) => {
      let activeCol = null;
      let overCol = null;
      for (const [col, items] of Object.entries(prev)) {
        if (items.includes(active.id)) activeCol = col;
        if (items.includes(over.id)) overCol = col;
      }
      if (!activeCol || !overCol) return prev;

      if (activeCol === overCol) {
        // Same column — reorder
        const oldIndex = prev[activeCol].indexOf(active.id);
        const newIndex = prev[activeCol].indexOf(over.id);
        if (oldIndex === newIndex) return prev;
        return { ...prev, [activeCol]: arrayMove(prev[activeCol], oldIndex, newIndex) };
      } else {
        // Cross-column — move active to over's column at over's position
        const activeItems = prev[activeCol].filter((id) => id !== active.id);
        const overItems = [...prev[overCol]];
        const overIdx = overItems.indexOf(over.id);
        overItems.splice(overIdx >= 0 ? overIdx : overItems.length, 0, active.id);
        return { ...prev, [activeCol]: activeItems, [overCol]: overItems };
      }
    });
  }, []);

  const agendaCardIds = extractAgendaCardIds(preCallData);

  const toggleLens = useCallback((program) => {
    setActiveLenses((prev) => {
      const next = new Set(prev);
      const summaryId = PROGRAM_SUMMARY_CARD_IDS[program];
      const cardIds = PROGRAM_CARD_IDS[program] || [];

      if (next.has(program)) {
        next.delete(program);
        setColumnOrders((prevOrders) => {
          const updated = { ...prevOrders };
          for (const [col, items] of Object.entries(updated)) {
            if (items.includes(summaryId)) {
              updated[col] = items.filter((id) => id !== summaryId);
            }
          }
          return updated;
        });
        setCollapsed((prevCollapsed) => {
          const stillClaimed = new Set();
          next.forEach((p) => {
            (PROGRAM_CARD_IDS[p] || []).forEach((id) => stillClaimed.add(id));
          });
          const nextCollapsed = { ...prevCollapsed };
          cardIds.forEach((id) => {
            if (!stillClaimed.has(id) && !ALWAYS_EXPANDED.has(id)) {
              nextCollapsed[id] = true;
            }
          });
          return nextCollapsed;
        });
      } else {
        next.add(program);
        setColumnOrders((prevOrders) => {
          const updated = { ...prevOrders };
          for (const [col, items] of Object.entries(updated)) {
            updated[col] = items.filter((id) => id !== summaryId);
          }
          updated.col1 = [summaryId, ...updated.col1];
          return updated;
        });
        setCollapsed((prevCollapsed) => {
          const nextCollapsed = { ...prevCollapsed };
          cardIds.forEach((id) => { nextCollapsed[id] = false; });
          nextCollapsed[summaryId] = false;
          return nextCollapsed;
        });
      }
      return next;
    });
  }, []);

  const getLensColor = useCallback(
    (cardId) => {
      if (agendaCardIds.has(cardId)) return null;
      for (const program of LENS_PRIORITY) {
        if (activeLenses.has(program) && (PROGRAM_CARD_IDS[program] || []).includes(cardId)) {
          return PROGRAM_COLORS[program];
        }
        if (activeLenses.has(program) && PROGRAM_SUMMARY_CARD_IDS[program] === cardId) {
          return PROGRAM_COLORS[program];
        }
      }
      return null;
    },
    [activeLenses, agendaCardIds]
  );

  return {
    columnOrders,
    collapsed,
    toggleCollapsed,
    expandAll,
    collapseAll,
    agendaCardIds,
    handleDragOver,
    handleDragEnd,
    activeLenses,
    toggleLens,
    getLensColor,
  };
}
